/**
 * Drawer de gestão da Base de Conhecimento (KB) de uma API Key do WhatsApi.
 * Proxy fino: WhatsApi é a fonte da verdade. Upload nativo (sem @mantine/dropzone).
 *
 * Serve tenant e organização: quem abre passa a `raiz` das rotas do escopo e o que o
 * usuário pode NELE (no tenant, `useCan`; na organização, o `pode` dela).
 */
import { useRef, useState } from 'react'
import {
  ActionIcon, Alert, Badge, Box, Button, Code, Drawer, Group, ScrollArea,
  Stack, Switch, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconRefresh, IconTrash, IconUpload, IconSearch } from '@tabler/icons-react'

import type { KbContextResult } from '@/api/whatsapi'
import {
  useKbFiles, useKbUpload, useKbToggle, useKbDelete, useKbReprocess, useKbContextPreview,
} from '@/hooks/useKbFiles'
import { KbStatusBadge } from './KbStatusBadge'

const ALLOWED = ['md', 'txt', 'pdf', 'docx', 'csv', 'json']
const MAX_BYTES = 10 * 1024 * 1024

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function validate(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!ALLOWED.includes(ext)) return `Tipo .${ext} não permitido (use: ${ALLOWED.join(', ')})`
  if (file.size > MAX_BYTES) return 'Arquivo excede 10 MB'
  if (file.size === 0) return 'Arquivo vazio'
  return null
}

/** Recusas das rotas de KB (tenant e organização) que a tela explica. */
const MOTIVO: Record<string, string> = {
  empty_file: 'Arquivo vazio',
  file_too_large: 'Arquivo excede 10 MB',
  kb_arquivo_recusado: 'O WhatsApi recusou o arquivo (tipo ou conteúdo).',
  kb_arquivo_nao_encontrado: 'Arquivo não encontrado no KB desta chave.',
  chave_nao_encontrada: 'A chave não é desta organização.',
  forbidden: 'Sem permissão para esta ação na organização.',
  whatsapi_indisponivel: 'WhatsApi indisponível. Tente de novo em instantes.',
  whatsapi_sem_credencial: 'WhatsApi indisponível. Tente de novo em instantes.',
  auditoria_falhou_kb_alterado: 'O KB mudou, mas a auditoria falhou. Avise o suporte.',
}

function motivoDaFalha(e: unknown, padrao: string): string {
  const err = e as { response?: { status?: number; data?: { detail?: unknown } } }
  if (err?.response?.status === 429) return 'Limite de requisições atingido. Aguarde e tente de novo.'
  const detail = err?.response?.data?.detail
  if (typeof detail !== 'string') return padrao
  return MOTIVO[detail] ?? detail
}

export interface PodeNoKb {
  criar: boolean
  atualizar: boolean
  apagar: boolean
}

interface KbDrawerProps {
  /** Raiz das rotas do WhatsApi do escopo: `raizDoTenant(id)` ou `raizDaOrg(id)`. */
  raiz: string
  apiKey: { id: string; name: string } | null
  pode: PodeNoKb
  opened: boolean
  onClose: () => void
}

export function KbDrawer({ raiz, apiKey, pode, opened, onClose }: KbDrawerProps) {
  const keyId = apiKey?.id ?? ''
  const { criar: canCreate, atualizar: canUpdate, apagar: canDelete } = pode

  const { data: files = [], isLoading, error } = useKbFiles(raiz, keyId, opened)
  const upload = useKbUpload(raiz, keyId)
  const toggle = useKbToggle(raiz, keyId)
  const remove = useKbDelete(raiz, keyId)
  const reprocess = useKbReprocess(raiz, keyId)
  const preview = useKbContextPreview(raiz, keyId)

  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [q, setQ] = useState('')
  const [ctx, setCtx] = useState<KbContextResult | null>(null)

  const handleFiles = (list: FileList | null) => {
    if (!list || !list.length) return
    const file = list[0]
    const err = validate(file)
    if (err) {
      notifications.show({ color: 'red', message: err })
      return
    }
    upload.mutate(file, {
      onSuccess: (res) => {
        if (res.deduped) notifications.show({ color: 'blue', message: 'Arquivo idêntico já existe (dedup).' })
        else notifications.show({ color: 'green', message: 'Upload enfileirado — processando.' })
      },
      onError: (e: unknown) => {
        notifications.show({ color: 'red', message: motivoDaFalha(e, 'Falha no upload') })
      },
    })
  }

  const confirmDelete = (fileId: string, filename: string) =>
    modals.openConfirmModal({
      title: 'Deletar arquivo de KB',
      children: <Text size="sm">Remover <Code>{filename}</Code>? Esta ação remove S3 + chunks e não pode ser desfeita.</Text>,
      labels: { confirm: 'Deletar', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(fileId, {
        onError: (e) => notifications.show({ color: 'red', message: motivoDaFalha(e, 'Falha ao deletar') }),
      }),
    })

  const runPreview = () => {
    if (!q.trim()) return
    preview.mutate(q.trim(), {
      onSuccess: setCtx,
      onError: (e) => notifications.show({
        color: 'red', message: motivoDaFalha(e, 'Falha ao pré-visualizar contexto'),
      }),
    })
  }

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="xl"
      title={<Title order={4}>Base de Conhecimento — {apiKey?.name}</Title>}>
      <Stack gap="md">
        {canCreate && (
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
            style={{
              border: `2px dashed var(--mantine-color-${dragging ? 'blue' : 'gray'}-4)`,
              borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer',
            }}
            onClick={() => inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" hidden accept=".md,.txt,.pdf,.docx,.csv,.json"
              onChange={(e) => { handleFiles(e.currentTarget.files); e.currentTarget.value = '' }} />
            <Group justify="center" gap="xs">
              <IconUpload size={20} />
              <Text size="sm">Arraste um arquivo ou clique para enviar (md, txt, pdf, docx, csv, json — máx 10 MB)</Text>
            </Group>
            {upload.isPending && <Text size="xs" c="dimmed" mt="xs">Enviando…</Text>}
          </Box>
        )}

        {error && <Alert color="red" variant="light">Não foi possível carregar os arquivos.</Alert>}

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Arquivo</Table.Th><Table.Th>Tipo</Table.Th><Table.Th>Tamanho</Table.Th>
              <Table.Th>Classe</Table.Th><Table.Th>Status</Table.Th><Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr><Table.Td colSpan={6}><Text size="sm" c="dimmed">Carregando…</Text></Table.Td></Table.Tr>
            ) : files.length === 0 ? (
              <Table.Tr><Table.Td colSpan={6}><Text size="sm" c="dimmed">Nenhum arquivo de KB.</Text></Table.Td></Table.Tr>
            ) : files.map((f) => (
              <Table.Tr key={f.id}>
                <Table.Td><Text size="sm">{f.filename}</Text></Table.Td>
                <Table.Td><Code>{f.content_type}</Code></Table.Td>
                <Table.Td><Text size="xs">{fmtSize(f.size_bytes)}</Text></Table.Td>
                <Table.Td>{f.classification && <Badge variant="outline" size="xs">{f.classification}</Badge>}</Table.Td>
                <Table.Td><KbStatusBadge status={f.status} error={f.error} /></Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    {canUpdate && (
                      <Tooltip label={f.is_active ? 'Desativar' : 'Ativar'}>
                        <Switch checked={f.is_active}
                          aria-label={`${f.is_active ? 'Desativar' : 'Ativar'} ${f.filename}`}
                          onChange={() => toggle.mutate({ fileId: f.id, isActive: !f.is_active })} />
                      </Tooltip>
                    )}
                    {canUpdate && (
                      <Tooltip label="Reprocessar">
                        <ActionIcon variant="subtle" color="blue" aria-label={`Reprocessar ${f.filename}`}
                          onClick={() => reprocess.mutate(f.id)}><IconRefresh size={16} /></ActionIcon>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip label="Deletar">
                        <ActionIcon variant="subtle" color="red" aria-label={`Deletar ${f.filename}`}
                          onClick={() => confirmDelete(f.id, f.filename)}><IconTrash size={16} /></ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Title order={5} mt="md">Pré-visualizar contexto</Title>
        <Group>
          <TextInput flex={1} placeholder="ex: qual o horário de atendimento" value={q}
            aria-label="Consulta de contexto"
            onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runPreview() }} />
          <Button leftSection={<IconSearch size={16} />} loading={preview.isPending} onClick={runPreview}>Buscar</Button>
        </Group>
        {ctx && (
          <Stack gap="xs">
            <Group gap="xs">
              {ctx.degraded && <Badge color="orange" variant="light">degraded</Badge>}
              {ctx.truncated && <Badge color="yellow" variant="light">truncated</Badge>}
              {ctx.sources.map((s, i) => (
                <Badge key={i} variant="outline" size="xs">
                  {s.filename} · {s.kind}{s.score != null ? ` · ${s.score.toFixed(2)}` : ''}
                </Badge>
              ))}
            </Group>
            <ScrollArea.Autosize mah={240}>
              <Code block>{ctx.context}</Code>
            </ScrollArea.Autosize>
          </Stack>
        )}
      </Stack>
    </Drawer>
  )
}
